import React, { Suspense } from 'react';
import { HashRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { MasterDataProvider } from './context/MasterDataContext';
import { StructureProvider } from './context/StructureContext';
import { GuruProvider } from './context/GuruContext';
import { SantriProvider } from './context/SantriContext';
import { KlinikProvider } from './context/KlinikContext';
import { KegiatanSantriProvider } from './context/KegiatanSantriContext';
import { AbsensiLiburanProvider } from './context/AbsensiLiburanContext';
import { UserAccessProvider } from './context/UserAccessContext';
import { SessionProvider } from './context/SessionContext';
import { PermitProvider } from './context/PermitContext';
import { PWAProvider } from './context/PWAContext';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { ReloadPrompt } from './components/ReloadPrompt';
import { PushNotificationManager } from './components/notifications/PushNotificationManager';
import DataInitializer from './components/DataInitializer';
import { PortalGuruRoutes } from './routes/PortalGuruRoutes';
import { PortalWaliSantriRoutes } from './routes/PortalWaliSantriRoutes';
import { InactiveAccountScreen } from './components/InactiveAccountScreen';
import { ActiveGuruRoute } from './components/ActiveGuruRoute';
import { KegiatanAbsenRoutes } from './routes/KegiatanAbsenRoutes';
import { LoadingSpinner } from './components/LoadingSpinner';
import { useMasterDataAutoFetch } from './hooks/useMasterDataAutoFetch';
import { ScrollToTop } from './components/ScrollToTop';
import { MaintenanceGate } from './components/maintenance/MaintenanceGate';

// Lazy load components

const LoginScreen = React.lazy(() => import('./components/LoginScreen').then(module => ({ default: module.LoginScreen })));
const BingkaiMiasPublic = React.lazy(() => import('./components/BingkaiMiasPublic').then(module => ({ default: module.BingkaiMiasPublic })));
const PermitPublicDetail = React.lazy(() => import('./components/perizinan/PermitPublicDetail').then(module => ({ default: module.PermitPublicDetail })));
const PublicInventarisView = React.lazy(() => import('./components/inventaris/PublicInventarisView').then(module => ({ default: module.PublicInventarisView })));



const MasterDataAutoFetcher: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useMasterDataAutoFetch();
  return <>{children}</>;
};

const AuthenticatedAppProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <MasterDataProvider>
      <StructureProvider>
      <GuruProvider>
        <SantriProvider>
          <UserAccessProvider>
            <SessionProvider>
              <KlinikProvider>
                <KegiatanSantriProvider>
                  <AbsensiLiburanProvider>
                    <PermitProvider>
                      <MasterDataAutoFetcher>
                        <DataInitializer />
                        {children}
                      </MasterDataAutoFetcher>
                    </PermitProvider>
                  </AbsensiLiburanProvider>
                </KegiatanSantriProvider>
              </KlinikProvider>
            </SessionProvider>
          </UserAccessProvider>
        </SantriProvider>
      </GuruProvider>
    </StructureProvider>
    </MasterDataProvider>
  );
};

const AppContent: React.FC = () => {
  return (
     <HashRouter>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public Routes - No Authenticated Data Providers */}
            <Route path="/" element={<LoginScreen />} />
            <Route path="/bingkaimias" element={<BingkaiMiasPublic />} />
            <Route path="/perizinan/detail/:id" element={<PermitPublicDetail />} />
            <Route path="/inventaris/public/:gedungId" element={<PublicInventarisView />} />
            <Route path="/nonaktif" element={<AuthenticatedAppProviders><InactiveAccountScreen /></AuthenticatedAppProviders>} />

            {/* Private Routes - Wrapped in Authenticated Data Providers */}
            <Route path="/portal-wali/*" element={<AuthenticatedAppProviders><PortalWaliSantriRoutes /></AuthenticatedAppProviders>} />
            {React.Children.map(KegiatanAbsenRoutes, child => {
              if (React.isValidElement(child)) {
                return React.cloneElement(child, {
                  element: <AuthenticatedAppProviders>{child.props.element}</AuthenticatedAppProviders>
                });
              }
              return child;
            })}
            <Route path="/*" element={<AuthenticatedAppProviders><ActiveGuruRoute><PortalGuruRoutes /></ActiveGuruRoute></AuthenticatedAppProviders>} />
          </Routes>
        </Suspense>
      </HashRouter>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <PWAProvider>
        <MaintenanceGate>
          <AppContent />
        </MaintenanceGate>
        <PWAInstallPrompt />
        <ReloadPrompt />
        <PushNotificationManager />
      </PWAProvider>
    </AuthProvider>
  );
};

export default App;
